import { formatNumber } from './format-numbers';
/**
 * Shared vocabulary for the grouped inventory / STR data returned by
 * `/inventory/str` and `/inventory/impressions`.
 *
 * Both endpoints group impressions by the same three dimensions and use the
 * same metric names, so the STR page and the Inventory page can share labels
 * and formatting.
 */

export type GroupSource = 'anghami' | 'gam';
export type GroupAdType = 'audio' | 'video';
export type GroupPlacement = 'foreground' | 'background';

export type GroupFilter<T extends string> = T | 'all';

/** One grouped row as returned by the API, before any client-side summing. */
export interface GroupMetrics {
  total_impressions: number;
  reserved_impressions: number;
  house_impressions: number;
  /** 100 * reserved / total, two decimals, as computed by the API. */
  str: number;
}

export interface GroupRow extends GroupMetrics {
  source: GroupSource;
  ad_type: GroupAdType;
  placement: GroupPlacement;
}

export const SOURCE_LABELS: Record<GroupSource, string> = {
  anghami: 'Anghami',
  gam: 'GAM'
};

export const AD_TYPE_LABELS: Record<GroupAdType, string> = {
  audio: 'Audio',
  video: 'Video'
};

export const PLACEMENT_LABELS: Record<GroupPlacement, string> = {
  foreground: 'Foreground',
  background: 'Background'
};

const withAll = <T extends string>(labels: Record<T, string>, allText: string) => [
  { id: 'all', text: allText },
  ...(Object.keys(labels) as T[]).map((id) => ({ id, text: labels[id] }))
];

export const sourceFilterOptions = withAll(SOURCE_LABELS, 'All sources');
export const adTypeFilterOptions = withAll(AD_TYPE_LABELS, 'Audio + Video');
export const placementFilterOptions = withAll(PLACEMENT_LABELS, 'All placements');

export const percentage = (part: number, whole: number, decimals = 2): number =>
  whole > 0 ? Number(((100 * part) / whole).toFixed(decimals)) : 0;

/** Sums the metric columns of grouped rows, recomputing the percentage. */
export const sumGroupMetrics = (rows: readonly GroupMetrics[]): GroupMetrics => {
  // `|| 0` keeps the sums numeric if an older API omits a metric.
  const total_impressions = rows.reduce((sum, row) => sum + (row.total_impressions || 0), 0);
  const reserved_impressions = rows.reduce((sum, row) => sum + (row.reserved_impressions || 0), 0);
  const house_impressions = rows.reduce((sum, row) => sum + (row.house_impressions || 0), 0);
  return {
    total_impressions,
    reserved_impressions,
    house_impressions,
    str: percentage(reserved_impressions, total_impressions)
  };
};

/** Stable display order: source, then ad type, then placement. */
export const sortGroupRows = <T extends GroupRow>(rows: readonly T[] | null | undefined): T[] =>
  [...(rows || [])].sort((a, b) =>
    `${a.source}|${a.ad_type}|${a.placement}`.localeCompare(
      `${b.source}|${b.ad_type}|${b.placement}`
    )
  );

/**
 * Number formatting for capacity metrics. Unlike `formatNumber`, a real zero
 * is shown as "0"; only a missing value renders as a dash.
 */
export const formatCount = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '-';
  }
  return value === 0 ? '0' : formatNumber(value);
};
